import { Injectable } from '@angular/core';
import {
	addressString,
	getRegionName,
	getRegionType,
} from '@core/util/address';
import { clientLegalName, isGuarantor } from '@core/util/client';
import { DateToYearMonthDay } from '@core/util/datetime';
import { formatCentsToDollars } from '@core/util/money';
import { joinNames, splitSentenceByCharactersLimit } from '@core/util/string';
import { Business, BusinessAddress, BusinessUser } from '@models/businesses';
import { AddressFormat } from '@models/businessSettings/businessFormatTypes';
import {
	AddressFormattingSettings,
	NameFormattingSettings,
} from '@models/businessSettings/settings';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	MatterTitle,
	ClientsCapacity,
	MatterFileType,
} from '@models/matter';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../base-form-mapper';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { Jurisdiction } from '@models/jurisdictions';
import { MapAddress } from '@state/global/tags/mappings/tags.address';
import { getLTTLegalDescription } from '@core/util/legalDescription';

@Injectable({
	providedIn: 'root',
})
export class LTT_REBATE_FORM_Mapper extends BaseFormMapper<LTT_REBATE_FORM> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: LTT_REBATE_FORM,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		_sellers: (PersonMatterClient | BusinessMatterClient)[],
		_buyers: (PersonMatterClient | BusinessMatterClient)[],
		_adjustments: AllAdjustmentsCalculated,
		_business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		_clientsCapacity: ClientsCapacity,
		_mortgage?: MatterMortgageWithAssociations,
		_businessUsers?: BusinessUser[],
		primaryJurisdiction?: Jurisdiction,
		_signors?: PersonMatterClientWithAssociations[],
		_otherPartiesWithAssociations?: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		_matterFileType?: MatterFileType,
		addressFormattingSettings?: AddressFormattingSettings,
	) {
		const _formData = formData ? cloneDeep(formData) : ({} as LTT_REBATE_FORM);

		const FilteredMatterClients = matterClients?.filter(
			(client) => !isGuarantor(client),
		);

		const propertyAddress = MapAddress(
			matter?.propertyDetails?.address,
			addressFormattingSettings || { format: AddressFormat.FORMAT_1 },
		);

		const legalDescription = getLTTLegalDescription(
			matterTitles,
			matter?.propertyDetails?.propertyType?.type,
			primaryJurisdiction,
			propertyAddress,
		);

		if (legalDescription) {
			const splittedDescription = splitSentenceByCharactersLimit(
				legalDescription,
				110,
			);
			if (
				(!_formData?.conveyanceOf || _formData?.conveyanceOf?.length === 0) &&
				splittedDescription.length >= 1
			) {
				_formData.conveyanceOf = splittedDescription[0];
			}

			if (
				(!_formData?.conveyanceOf2 || _formData?.conveyanceOf2?.length === 0) &&
				splittedDescription.length >= 2
			) {
				_formData.conveyanceOf2 = splittedDescription
					.map((description, index) => {
						if (index !== 0) {
							return description;
						}
					})
					.join(' ');
			}
		}

		if (
			(!_formData?.by || _formData?.by?.length === 0) &&
			matterClients.length > 0
		) {
			_formData.by = joinNames(
				otherParties.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			(!_formData?.to || _formData?.to?.length === 0) &&
			matterClients.length > 0
		) {
			_formData.to = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			(!_formData?.oathCheckboxDate3 ||
				_formData?.oathCheckboxDate3?.length === 0) &&
			matter?.occupancyDate
		) {
			_formData.oathCheckboxDate3 = DateToYearMonthDay(matter?.occupancyDate);
		}

		if (
			(!_formData?.oathCheckboxDate4 ||
				_formData?.oathCheckboxDate4?.length === 0) &&
			matter?.agreementDate
		) {
			_formData.oathCheckboxDate4 = DateToYearMonthDay(matter?.agreementDate);
		}

		if (
			(!_formData?.totalPriceExcludingHST ||
				_formData?.totalPriceExcludingHST?.length === 0) &&
			typeof lttCalculated?.subjectToLtt === 'number'
		) {
			_formData.totalPriceExcludingHST = formatCentsToDollars(
				lttCalculated.nsp,
				false,
			);
		}

		if (
			(!_formData?.landTransferTaxPaid ||
				_formData?.landTransferTaxPaid?.length === 0) &&
			typeof lttCalculated?.lttCalculation?.tax === 'number'
		) {
			_formData.landTransferTaxPaid = formatCentsToDollars(
				lttCalculated?.lttCalculation?.tax,
				false,
			);
		}

		if (
			(!_formData?.landTransferTaxRefund ||
				_formData?.landTransferTaxRefund?.length === 0) &&
			typeof lttCalculated?.lttCalculation?.refund === 'number'
		) {
			_formData.landTransferTaxRefund = formatCentsToDollars(
				lttCalculated?.lttCalculation?.refund,
				false,
			);
		}

		if (
			(!_formData?.netLandTransferTaxPaid ||
				_formData?.netLandTransferTaxPaid?.length === 0) &&
			typeof lttCalculated?.lttCalculation?.payable === 'number'
		) {
			_formData.netLandTransferTaxPaid = formatCentsToDollars(
				lttCalculated?.lttCalculation?.payable,
				false,
			);
		}

		if (
			(!_formData?.refundAmount || _formData?.refundAmount?.length === 0) &&
			typeof lttCalculated?.lttCalculation?.refund === 'number'
		) {
			_formData.refundAmount = formatCentsToDollars(
				lttCalculated?.lttCalculation?.refund,
				false,
			);
		}

		if (
			(!_formData?.swornBefore || _formData?.swornBefore?.length === 0) &&
			businessAddress?.city
		) {
			_formData.swornBefore = `City of ${businessAddress?.city}`;
		}

		if (
			(!_formData?.inThe || _formData?.inThe?.length === 0) &&
			businessAddress?.province
		) {
			_formData.inThe = `${getRegionType(businessAddress?.province, businessAddress?.country)} of ${getRegionName(businessAddress?.province, businessAddress?.country)}`;
		}

		if (matter?.closingDate) {
			const date = new Date(matter?.closingDate);
			if (!_formData?.month || _formData?.month?.length === 0) {
				_formData.month = format(date, 'MMMM');
			}

			if (!_formData?.day || _formData?.day?.length === 0) {
				_formData.day = format(date, 'dd');
			}

			if (!_formData?.year || _formData?.year?.length === 0) {
				_formData.year = format(date, 'yy');
			}
		}

		if (
			(!_formData?.addressOfHomepurchased ||
				_formData?.addressOfHomepurchased?.length === 0) &&
			propertyAddress?.lines2?.line1
		) {
			_formData.addressOfHomepurchased = propertyAddress?.lines2?.line1 ?? '';
		}

		if (
			(!_formData?.addressOfHomepurchased1 ||
				_formData?.addressOfHomepurchased1?.length === 0) &&
			propertyAddress?.lines2?.line2
		) {
			_formData.addressOfHomepurchased1 = propertyAddress?.lines2?.line2 ?? '';
		}

		if (
			(!_formData?.nameAndTransfereeSolicitor ||
				_formData?.nameAndTransfereeSolicitor?.length === 0) &&
			matter?.lawyerAssigned
		) {
			_formData.nameAndTransfereeSolicitor = matter?.lawyerAssigned?.name;
		}

		if (
			(!_formData?.nameAndTransfereeSolicitor1 ||
				_formData?.nameAndTransfereeSolicitor1?.length === 0) &&
			businessAddress
		) {
			_formData.nameAndTransfereeSolicitor1 = `${addressString(businessAddress)}`;
		}

		if (
			(!_formData?.nameAndTransfereeSolicitor2 ||
				_formData?.nameAndTransfereeSolicitor2?.length === 0) &&
			matter?.number
		) {
			_formData.nameAndTransfereeSolicitor2 = `[File ${matter?.number ?? ''}]`;
		}

		if (
			(!_formData?.solicitorTelephoneNumber ||
				_formData?.solicitorTelephoneNumber?.length === 0) &&
			businessAddress?.phoneNumber
		) {
			_formData.solicitorTelephoneNumber = businessAddress?.phoneNumber;
		}

		return _formData;
	}

	MapValues(): Partial<LTT_REBATE_FORM> {
		return {};
	}
}

const Mappings: LTT_REBATE_FORM = {
	registrationNumber: 'Text1',
	date: 'Text2',
	conveyanceOf: 'Text3',
	conveyanceOf2: 'Text4',
	by: 'Text5',
	to: 'Text6',
	names1: 'CC007',
	names2: 'Text8',
	oathCheckbox1: 'CC009',
	oathCheckbox2: 'CC010',
	oathCheckbox3: 'CC011',
	oathCheckboxDate3: 'CC013',
	oathCheckbox4: 'CC012',
	oathCheckboxDate4: 'CC014',
	totalPriceExcludingHST: 'Text15',
	landTransferTaxPaid: 'Text16',
	landTransferTaxRefund: 'Text17',
	netLandTransferTaxPaid: 'Text18',
	refundAmount: 'Text19',
	accountHolders: 'Text7',
	accountType: 'Text9',
	branchNo: 'Text10',
	institutionNo: 'Text11',
	accountNo: 'Text12',
	directDepositDate: 'Text13',
	swornBefore: 'Text20',
	inThe: 'Text23',
	day: 'Text24',
	month: 'Text25',
	year: 'Text26',
	spouseName: 'Text27',
	firstTimeHomePurchaserName: 'Text28',
	addressOfHomepurchased: 'Text29',
	addressOfHomepurchased1: 'Text30',
	addressOfHomepurchased2: 'Text31',
	nameAndTransfereeSolicitor: 'Text32',
	nameAndTransfereeSolicitor1: 'Text33',
	nameAndTransfereeSolicitor2: 'Text34',
	telephoneNumber: 'Text35',
	solicitorTelephoneNumber: 'Text36',
};
export interface LTT_REBATE_FORM {
	registrationNumber: string;
	date: string;
	conveyanceOf: string;
	conveyanceOf2: string;
	by: string;
	to: string;
	names1: string;
	names2: string;
	oathCheckbox1: string;
	oathCheckbox2: string;
	oathCheckbox3: string;
	oathCheckboxDate3: string;
	oathCheckbox4: string;
	oathCheckboxDate4: string;
	totalPriceExcludingHST: string;

	landTransferTaxPaid: string;
	landTransferTaxRefund: string;
	netLandTransferTaxPaid: string;
	refundAmount: string;
	accountHolders: string;
	accountType: string;
	branchNo: string;
	institutionNo: string;
	accountNo: string;
	directDepositDate: string;

	swornBefore: string;
	inThe: string;
	day: string;
	month: string;
	year: string;

	spouseName: string;
	firstTimeHomePurchaserName: string;
	addressOfHomepurchased: string;
	addressOfHomepurchased1: string;
	addressOfHomepurchased2: string;
	nameAndTransfereeSolicitor: string;
	nameAndTransfereeSolicitor1: string;
	nameAndTransfereeSolicitor2: string;
	telephoneNumber: string;
	solicitorTelephoneNumber: string;
}
